import React, { useEffect, useState } from 'react';
import { FormBuilder } from '@formio/react';
import { TextField, Button, Typography, FormGroup, FormControlLabel, Checkbox, Box, Dialog, DialogTitle, DialogContent, IconButton, Autocomplete } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios'
import { APIURL } from '../../../config';
import '../builderSidebarSticky.css';
import { hasAiCompanion } from 'src/utils/utilities';

export default function FormsBuilder(props) {
    const [open, setOpen] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formType, setFormType] = useState('');
    const [formSchema, setFormSchema] = useState({});
    const [formComponents, setFormComponents] = useState(0);
    const [maxFormID, setMaxFormID] = useState(0);
    const [isPublicForm, setIsPublicForm] = useState(0);
    const [aiQualityCheck, setAiQualityCheck] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [errorAlert, setErrorAlert] = useState('');
    const [successAlert, setSuccessAlert] = useState('');
    const [infoOpen, setInfoOpen] = useState(false);
    const [confirmCommunityOpen, setConfirmCommunityOpen] = useState(false);

    const languages = [
        { value: 'es', label: 'Spanish' },
    ];

    useEffect(() => {
        const fetchMaxFormID = async () => {
            try {
                const res = await axios.get(`${APIURL}/task_form/formBuilder`);
                setMaxFormID(res.data[0][""]);
                setFormTitle('');
                setFormDesc('');
                setFormType('');
            } catch (error) {
                console.error('Error fetching max form ID:', error);
            }
        };
        fetchMaxFormID();
    }, [open]);

    const handleFormSchemaChange = (schema) => {
        setFormSchema(schema);
        setFormComponents((schema.components || []).length);
    };

    const getInfoBox = () => {
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 40,
                    zIndex: 10,
                    backgroundColor: '#fafafa',
                    border: '1px solid #ccc',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    fontFamily: 'Lato',
                    boxShadow: 2,
                    width: '500px',
                    animation: 'fadeIn 0.3s',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    📋 How to use the Form Builder
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                    With this tool, you can easily create custom forms tailored to your needs.
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Getting Started:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    Drag and drop desired form components from the left panel onto the canvas.
                    Hover over a component to customize its properties or remove it. Use the
                    "Move" button to reposition components. Once satisfied, click "Add Form" to
                    save it.
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Supported Components:
                </Typography>
                <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                    <li>
                        <Typography variant="body2">
                            <strong>Basic Section:</strong> All fields.
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body2">
                            <strong>Advanced Section:</strong> Date/Time, Day and Time.
                        </Typography>
                    </li>
                </ul>
            </Box>
        );
    };

    const getAddForm = () => {
        return (
            <Dialog fullWidth={true} maxWidth={'sm'} open={open} onClose={() => setOpen(false)}>
                <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                    Add Form
                </DialogTitle>
                <DialogContent sx={{ paddingTop: '0px' }}>
                    <TextField
                        label="Form Title *"
                        value={formTitle}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormTitle(event.target.value)}
                        fullWidth
                        error={!formTitle}
                    />
                    <TextField
                        label="Form Description"
                        value={formDesc}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormDesc(event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Form Type *"
                        value={formType}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormType(event.target.value)}
                        fullWidth
                        error={!formType}
                    />
                    <Autocomplete
                        id="language-select"
                        options={languages}
                        value={selectedLanguage}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(event, value) => { setSelectedLanguage(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.value}>
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Language" helperText="Language the form will be translated to. Default: English" />}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox
                                checked={isPublicForm}
                                onChange={(event) => setIsPublicForm(event.target.checked ? 1 : 0)}
                            />}
                            label="Community Form"
                        />
                        {/* Only offered to companies that have bought the module. */}
                        {hasAiCompanion(props.user) && (
                            <FormControlLabel
                                control={<Checkbox
                                    checked={aiQualityCheck}
                                    onChange={(event) => setAiQualityCheck(event.target.checked ? 1 : 0)}
                                />}
                                label="Enable AI completeness check"
                            />
                        )}
                    </FormGroup>
                    <Button
                        variant='contained'
                        fullWidth
                        // Community forms are locked once saved, so confirm first
                        onClick={() => isPublicForm ? setConfirmCommunityOpen(true) : addForm()}
                        disabled={!formTitle || !formType}
                    >
                        Add Form
                    </Button>
                </DialogContent>
            </Dialog>
        );
    };

    const getConfirmCommunity = () => {
        return (
            <Dialog open={confirmCommunityOpen} onClose={() => setConfirmCommunityOpen(false)}>
                <DialogTitle>Save as a Community Form?</DialogTitle>
                <DialogContent>
                    <div>Community forms can't be edited or deleted once they're saved, and they're shared with every company in the Community Forms library. To make changes later, you'd need to copy the form.</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                        <Button onClick={() => setConfirmCommunityOpen(false)}>Cancel</Button>
                        <Button onClick={() => { setConfirmCommunityOpen(false); addForm(); }} variant="contained" color="primary">Save as Community Form</Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    };

    const addForm = async () => {
        if (formTitle.replace(/\s/g, '').length !== 0 && formType.replace(/\s/g, '').length !== 0) {
            await axios.post(APIURL + `/task_form/formBuilder`, {
                form_id: maxFormID + 1,
                form_name: formTitle,
                form_description: formDesc,
                form_added_by: props.user.emp_id,
                form_type: formType,
                json_schema: formSchema,
                form_company: props.comp_id,
                public_form: isPublicForm,
                ai_quality_check: aiQualityCheck,
                form_language: selectedLanguage ? selectedLanguage.value : null
            })
                .then(function (response) {
                    setSuccessAlert('Form successfully added')
                })
                .catch(function (error) {
                    console.log(error);
                    setErrorAlert('An error occured while trying to add the form')
                });
        } else {
            if (!formTitle) {
                alert(`Form Title is required to add the form`)
            }
            else if (!formType) {
                alert(`Form Type is required to add the form`)
            } else {
                alert(`Please enter the required fields to add the form`)
            }
        }
    };

    const handleErrorAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setErrorAlert('');
        setOpen(false);
    }

    const handleSuccessAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccessAlert('');
        setOpen(false);
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex' }}>
                    <div>
                        <h5>Welcome to the Form Builder!</h5>
                    </div>
                    <Box sx={{ position: 'relative', marginLeft: 1 }}>
                        <IconButton sx={{ p: 0 }} onClick={() => setInfoOpen(!infoOpen)}>
                            <InfoIcon color='primary' />
                        </IconButton>
                        {infoOpen && getInfoBox()}
                    </Box>
                </div>
                <div>
                    <Button variant='contained' onClick={() => setOpen(true)} disabled={formComponents <= 1}>
                        Add Form
                    </Button>
                </div>
            </div>
            <div className="formbuilder-scroll-wrap" style={{ border: '1px solid #ccc', padding: 10, marginTop: 10 }}>
                <FormBuilder form={{ display: 'form' }} options={{ builder: { premium: false } }} onChange={handleFormSchemaChange} />
            </div>
            <div>
                {getAddForm()}
                {getConfirmCommunity()}
            </div>
            <Snackbar open={successAlert != ''} autoHideDuration={2000} onClose={handleSuccessAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"success"} elevation={6} variant="filled" {...props}>
                    {successAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleSuccessAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>
            <Snackbar open={errorAlert != ''} autoHideDuration={2000} onClose={handleErrorAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"error"} elevation={6} variant="filled" {...props}>
                    {errorAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleErrorAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>
        </div>
    )
}